import type {
  QuestionTrend,
  QuestionWithScore,
  TeamHealthMetrics,
  TeamSurveys,
} from '../../api/queries';
import { normalizeQuestion } from '../../components/AnalysisLayout/analysis.utils';
import { getHealthCategoryKey, type HealthCategoryKey } from '../../utils/healthCategory';

export type TrendDirection = 'up' | 'down' | 'stable';

/** Endringer under 2 % regnes som stabile */
const STABLE_THRESHOLD_PERCENTAGE = 2;
const TOP_QUESTIONS_COUNT = 3;

export const trendTones: Record<TrendDirection, 'success' | 'danger' | 'neutral'> = {
  up: 'success',
  down: 'danger',
  stable: 'neutral',
};

const latestScore = (questionTrend: QuestionTrend): number | undefined => {
  const score = questionTrend.trends?.[questionTrend.trends.length - 1]?.score;
  return score === undefined || score === null || isNaN(score) ? undefined : score;
};

/** Retningen fra nest siste til siste måling av spørsmålet */
export const getTrendDirection = (questionTrend: QuestionTrend): TrendDirection => {
  const trends = questionTrend.trends ?? [];
  if (trends.length < 2) return 'stable';

  const latest = trends[trends.length - 1].score;
  const previous = trends[trends.length - 2].score;
  if (previous === 0 || isNaN(previous) || isNaN(latest)) return 'stable';

  const change = ((latest - previous) / previous) * 100;
  if (Math.abs(change) < STABLE_THRESHOLD_PERCENTAGE) return 'stable';
  return change > 0 ? 'up' : 'down';
};

export type TrendSummary = {
  questionCount: number;
  /** Snittet av siste måling for hvert spørsmål, eller null når ingen har score */
  average: number | null;
  top: Array<{ id: number; question: string; score: number; direction: TrendDirection }>;
};

/** Antall spørsmål, snitt og de tre spørsmålene med høyest siste score */
export const summarizeTrends = (questionTrends: QuestionTrend[]): TrendSummary => {
  const scored = questionTrends
    .map((questionTrend) => ({ questionTrend, score: latestScore(questionTrend) }))
    .filter((item): item is { questionTrend: QuestionTrend; score: number } => item.score !== undefined);

  const average = scored.length
    ? scored.reduce((sum, item) => sum + item.score, 0) / scored.length
    : null;

  const top = [...scored]
    .sort((a, b) => b.score - a.score)
    .slice(0, TOP_QUESTIONS_COUNT)
    .map(({ questionTrend, score }) => ({
      id: questionTrend.question.id,
      question: questionTrend.question.question,
      score,
      direction: getTrendDirection(questionTrend),
    }));

  return { questionCount: questionTrends.length, average, top };
};

export type QuestionRow = {
  key: string;
  question: string;
  average: number;
  latest?: number;
  direction?: TrendDirection;
};

/** Snittet i perioden per spørsmål, med siste måling og retning når trendene har samme spørsmål */
export const toQuestionRows = (
  questions: QuestionWithScore[],
  questionTrends: QuestionTrend[] = [],
): QuestionRow[] => {
  const trendsByQuestion = new Map(
    questionTrends.map((questionTrend) => [normalizeQuestion(questionTrend.question.question), questionTrend]),
  );
  return questions.map((question) => {
    const questionTrend = trendsByQuestion.get(normalizeQuestion(question.question));
    return {
      key: `${question.id}-${question.question}`,
      question: question.question,
      average: question.score,
      latest: questionTrend ? latestScore(questionTrend) : undefined,
      direction: questionTrend ? getTrendDirection(questionTrend) : undefined,
    };
  });
};

export type Area = {
  id: number;
  key: HealthCategoryKey | null;
  name: string;
  description: string;
  score: number;
  standardDeviation?: number;
};

/** Områdene med data, lavest score først. Det som trenger mest oppmerksomhet, står først. */
export const toAreas = (metrics: TeamHealthMetrics[]): Area[] =>
  metrics
    .filter((metric) => metric.score > 0)
    .map((metric) => ({
      id: metric.id,
      key: getHealthCategoryKey(metric.category),
      name: metric.category,
      description: metric.description,
      score: metric.score,
      standardDeviation: metric.standardDeviation,
    }))
    .sort((a, b) => a.score - b.score);

/** Samme vekting som handlingsplanen i backend (action-plan.service.ts) */
const FOCUS_WEIGHTS = { scoreGap: 10, standardDeviation: 5 } as const;

const focusPriority = (area: Area) =>
  (5 - area.score) * FOCUS_WEIGHTS.scoreGap + (area.standardDeviation ?? 0) * FOCUS_WEIGHTS.standardDeviation;

/**
 * Området teamet får som fokusområde i handlingsplanen: lav score og stor uenighet gir høy prioritet.
 * Må holdes lik selectFocusCategory i backend.
 */
export const getFocusArea = (areas: Area[]): Area | undefined =>
  areas.reduce<Area | undefined>(
    (focus, area) => (!focus || focusPriority(area) > focusPriority(focus) ? area : focus),
    undefined,
  );

const byNewest = (a: TeamSurveys, b: TeamSurveys) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

const hasResult = (survey: TeamSurveys): survey is TeamSurveys & { score: number } =>
  !survey.isActive &&
  survey.analyzeStatus === 'COMPLETED' &&
  survey.score !== null &&
  survey.score !== undefined;

/** Den siste fullførte målingen med nok svar til å vise resultat, og den før den */
export const getCompletedSurveys = (surveys: TeamSurveys[] = []) => {
  const completed = surveys.filter(hasResult).sort(byNewest);
  return { latest: completed[0], previous: completed[1] };
};
